import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, RefreshCw, Save, X } from 'lucide-react'
import {
  Button,
  ConfirmDialog,
  Drawer,
  FormField,
  FormRow,
  FormSection,
  HelpTip,
  ProductPageShell,
  SegmentedControl,
  StatePanel,
} from '@/components/common'
import { DecisionReview } from '@/components/patterns'
import { useOrganization } from '@/contexts/OrganizationContext'
import { igamingApi } from '@/services/api'
import { CollapsibleFieldGroup } from './shared/CollapsibleFieldGroup'
import { DataList } from './shared/DataList'
import { RuntimeImpact } from './shared/RuntimeImpact'
import {
  DEFAULT_JACKPOT_FORM,
  DEFAULT_MERCHANDISING_TAB,
  DEFAULT_PROMO_FORM,
  JACKPOT_TYPE_OPTIONS,
  PAGE_COPY,
  PROMO_STATUS_OPTIONS,
  PROMO_TYPE_OPTIONS,
} from './shared/ruleConstants'
import { formatDate } from './shared/formatters'
import { buildJackpotImpact, buildPromotionImpact } from './shared/impactHelpers'
import {
  buildJackpotPayload,
  buildPromoPayload,
  jackpotToForm,
  promoToForm,
} from './shared/payloadBuilders'
import { buildPayloadChangeItems } from '@/utils/payloadReview'
import styles from './shared/igaming.module.css'

const PROMOTION_REVIEW_FIELDS = {
  promo_id: { label: 'Promo ID', impact: 'Changes the runtime promotion identifier' },
  name: { label: 'Name', impact: 'Changes the operator-facing promotion identity' },
  promo_type: { label: 'Promo type', impact: 'Changes which rules can match this promotion' },
  status: { label: 'Status', impact: 'Controls runtime eligibility' },
  eligible_game_ids: { label: 'Eligible game IDs', impact: 'Changes the affected game set' },
  priority: { label: 'Priority', impact: 'Changes ordering when promotions compete' },
  market: { label: 'Regulation', impact: 'Changes regulation targeting' },
  segment: { label: 'Segment', impact: 'Changes player segment targeting' },
  starts_at: { label: 'Starts', impact: 'Changes when the promotion becomes eligible' },
  ends_at: { label: 'Ends', impact: 'Changes when the promotion stops being eligible' },
  is_active: { label: 'Active', impact: 'Controls inclusion in runtime configuration' },
}

const JACKPOT_REVIEW_FIELDS = {
  pool_id: { label: 'Pool ID', impact: 'Changes the runtime jackpot identifier' },
  jackpot_type: { label: 'Jackpot type', impact: 'Controls player-facing urgency and rule matching' },
  label: { label: 'Display label', impact: 'Changes the player-facing jackpot identity' },
  current_amount: { label: 'Current amount', impact: 'Changes the displayed jackpot value' },
  currency: { label: 'Currency', impact: 'Changes how the amount is interpreted' },
  participating_game_ids: { label: 'Participating game IDs', impact: 'Changes the affected game set' },
  must_drop_at: { label: 'Must drop at', impact: 'Changes the must-drop deadline' },
  last_payout_at: { label: 'Last payout', impact: 'Changes payout recency context' },
  market: { label: 'Regulation', impact: 'Changes regulation targeting' },
  segment: { label: 'Segment', impact: 'Changes player segment targeting' },
  is_active: { label: 'Active', impact: 'Controls inclusion in runtime configuration' },
}

function queryReviewState(query) {
  if (query.isLoading) return 'loading'
  if (query.isError) return 'unavailable'
  if (query.data === undefined || query.data === null) return 'unknown'
  return 'ready'
}

function withRuleEvidenceState(impact, state) {
  const message = {
    loading: 'Matching rules for this item are still loading.',
    unavailable: 'Matching rules for this item are unavailable.',
    unknown: 'Matching rules for this item are unknown.',
  }[state]

  if (!message) return impact
  return { ...impact, rules: [], warnings: [...impact.warnings, message] }
}

function IGamingMerchandisingPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const [promoForm, setPromoForm] = useState(DEFAULT_PROMO_FORM)
  const [jackpotForm, setJackpotForm] = useState(DEFAULT_JACKPOT_FORM)
  const [dataError, setDataError] = useState('')
  const [merchandisingTab, setMerchandisingTab] = useState(DEFAULT_MERCHANDISING_TAB)
  const [deleteCandidate, setDeleteCandidate] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)

  const canConfigure = hasProductAccess('igaming_search', 'configure')
  const pageCopy = PAGE_COPY.merchandising

  const rulesQuery = useQuery({
    queryKey: ['igaming', slug, 'rules'],
    queryFn: () => igamingApi.listRules(slug),
    enabled: Boolean(slug),
  })

  const promotionsQuery = useQuery({
    queryKey: ['igaming', slug, 'promotions'],
    queryFn: () => igamingApi.listPromotions(slug),
    enabled: Boolean(slug),
  })

  const jackpotsQuery = useQuery({
    queryKey: ['igaming', slug, 'jackpots'],
    queryFn: () => igamingApi.listJackpotPools(slug),
    enabled: Boolean(slug),
  })

  const savePromotion = useMutation({
    mutationFn: (payload) => promoForm.id
      ? igamingApi.updatePromotion(slug, promoForm.id, payload)
      : igamingApi.createPromotion(slug, payload),
    onSuccess: () => {
      toast.success('Promotion saved')
      setPromoForm(DEFAULT_PROMO_FORM)
      setEditorOpen(false)
      setDataError('')
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => setDataError(error.response?.data?.error || error.message || 'Failed to save promotion'),
  })

  const deletePromotion = useMutation({
    mutationFn: (id) => igamingApi.deletePromotion(slug, id),
    onSuccess: () => {
      toast.success('Promotion deleted')
      setPromoForm(DEFAULT_PROMO_FORM)
      setDeleteCandidate(null)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to delete promotion'),
  })

  const saveJackpot = useMutation({
    mutationFn: (payload) => jackpotForm.id
      ? igamingApi.updateJackpotPool(slug, jackpotForm.id, payload)
      : igamingApi.createJackpotPool(slug, payload),
    onSuccess: () => {
      toast.success('Jackpot pool saved')
      setJackpotForm(DEFAULT_JACKPOT_FORM)
      setEditorOpen(false)
      setDataError('')
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => setDataError(error.response?.data?.error || error.message || 'Failed to save jackpot pool'),
  })

  const deleteJackpot = useMutation({
    mutationFn: (id) => igamingApi.deleteJackpotPool(slug, id),
    onSuccess: () => {
      toast.success('Jackpot pool deleted')
      setJackpotForm(DEFAULT_JACKPOT_FORM)
      setDeleteCandidate(null)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to delete jackpot pool'),
  })

  const rules = rulesQuery.data?.rules || []
  const promotions = promotionsQuery.data?.items || []
  const jackpots = jackpotsQuery.data?.items || []
  const currentPromotion = promotions.find((item) => item.id === promoForm.id) || null
  const currentJackpot = jackpots.find((item) => item.id === jackpotForm.id) || null
  const promotionProposal = buildPromoPayload(promoForm)
  const jackpotProposal = buildJackpotPayload(jackpotForm)
  const promotionChanges = buildPayloadChangeItems({
    current: currentPromotion ? buildPromoPayload(promoToForm(currentPromotion)) : null,
    proposed: promotionProposal,
    fieldMetadata: PROMOTION_REVIEW_FIELDS,
    ignorePaths: ['impact_config'],
    defaultImpact: 'Changes promotion runtime behavior',
  })
  const jackpotChanges = buildPayloadChangeItems({
    current: currentJackpot ? buildJackpotPayload(jackpotToForm(currentJackpot)) : null,
    proposed: jackpotProposal,
    fieldMetadata: JACKPOT_REVIEW_FIELDS,
    ignorePaths: ['label_config'],
    defaultImpact: 'Changes jackpot runtime behavior',
  })
  const rulesReviewState = queryReviewState(rulesQuery)
  const rulesReady = rulesReviewState === 'ready'
  const promotionImpact = buildPromotionImpact(promotionProposal, rules, { rulesReady })
  const jackpotImpact = buildJackpotImpact(jackpotProposal, rules, { rulesReady })
  const matchingRulesReviewValue = {
    loading: 'Loading',
    unavailable: 'Unavailable',
    unknown: 'Unknown',
  }[rulesReviewState]
  const rulesReviewConsequence = {
    loading: 'Rule impact is still loading.',
    unavailable: 'Rule impact is unavailable. Retry the rules query before relying on this review.',
    unknown: 'Rule impact is unknown because rules have not loaded.',
  }[rulesReviewState]
  const deletePending = deletePromotion.isPending || deleteJackpot.isPending

  const merchandisingTabs = [
    { key: 'promotions', label: 'Promotions', count: promotions.length },
    { key: 'jackpots', label: 'Jackpots', count: jackpots.length },
  ]

  const submitDataForm = (event, mutation, payloadBuilder, formState, requiredFields) => {
    event.preventDefault()
    const missingField = requiredFields.find((field) => !String(formState[field] || '').trim())
    if (missingField) {
      setDataError('Fill in the required fields before saving.')
      return
    }
    mutation.mutate(payloadBuilder(formState))
  }

  return (
    <ProductPageShell
      title={pageCopy.title}
      subtitle={pageCopy.subtitle}
      actions={(
        <>
          <Button variant="secondary" onClick={() => {
            rulesQuery.refetch()
            promotionsQuery.refetch()
            jackpotsQuery.refetch()
          }}>
            <RefreshCw size={16} />
            Refresh
          </Button>
          {canConfigure ? (
            <Button onClick={() => {
              setDataError('')
              if (merchandisingTab === 'promotions') setPromoForm(DEFAULT_PROMO_FORM)
              else setJackpotForm(DEFAULT_JACKPOT_FORM)
              setEditorOpen(true)
            }}>
              <Plus size={16} />
              {merchandisingTab === 'promotions' ? 'Create promotion' : 'Create jackpot pool'}
            </Button>
          ) : null}
        </>
      )}
    >
      <div className={styles.stack}>
        {!canConfigure ? (
          <StatePanel
            state="restricted"
            title="Read-only merchandising"
            description="Configure access is required to create, edit, or delete promotions and jackpot pools."
            compact
          />
        ) : null}
        <SegmentedControl
          items={merchandisingTabs}
          value={merchandisingTab}
          label="Merchandising data type"
          onValueChange={(tab) => {
            setDataError('')
            setMerchandisingTab(tab)
          }}
        />
        {rulesQuery.isError ? (
          <StatePanel
            state="error"
            title="Rule impact is unavailable"
            description={rulesQuery.error?.response?.data?.error || rulesQuery.error?.message}
            onRetry={() => rulesQuery.refetch()}
            compact
          />
        ) : null}
        {merchandisingTab === 'promotions' && promotionsQuery.isLoading ? (
          <StatePanel state="loading" title="Loading promotions" compact />
        ) : null}
        {merchandisingTab === 'promotions' && promotionsQuery.isError ? (
          <StatePanel
            state="error"
            title="Promotions failed"
            description={promotionsQuery.error?.response?.data?.error || promotionsQuery.error?.message}
            onRetry={() => promotionsQuery.refetch()}
            compact
          />
        ) : null}
        {merchandisingTab === 'jackpots' && jackpotsQuery.isLoading ? (
          <StatePanel state="loading" title="Loading jackpot pools" compact />
        ) : null}
        {merchandisingTab === 'jackpots' && jackpotsQuery.isError ? (
          <StatePanel
            state="error"
            title="Jackpot pools failed"
            description={jackpotsQuery.error?.response?.data?.error || jackpotsQuery.error?.message}
            onRetry={() => jackpotsQuery.refetch()}
            compact
          />
        ) : null}
        {merchandisingTab === 'promotions' && (
          <div className={styles.stack}>
            <Drawer
              open={canConfigure && editorOpen && merchandisingTab === 'promotions'}
              side="right"
              title={promoForm.id ? 'Edit promotion' : 'Create promotion'}
              description="Set promotion eligibility and review its runtime effect before saving."
              onClose={() => {
                setEditorOpen(false)
                setPromoForm(DEFAULT_PROMO_FORM)
                setDataError('')
              }}
            >
              <FormSection title="Promotion details">
                <form className={styles.form} onSubmit={(event) => submitDataForm(event, savePromotion, buildPromoPayload, promoForm, ['promo_id', 'name'])}>
                  <FormRow>
                    <FormField label="Promo ID" required value={promoForm.promo_id} placeholder="spring-free-spins-2026" onChange={(event) => setPromoForm((current) => ({ ...current, promo_id: event.target.value }))} />
                    <FormField label="Name" required value={promoForm.name} placeholder="Spring Free Spins" onChange={(event) => setPromoForm((current) => ({ ...current, name: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Promo type" type="select" value={promoForm.promo_type} options={PROMO_TYPE_OPTIONS} onChange={(event) => setPromoForm((current) => ({ ...current, promo_type: event.target.value }))} />
                    <FormField label="Eligible game IDs" labelExtra={<HelpTip text="Comma-separated game codes. Multi-word names are not accepted here; use platform game IDs." label="Eligible game IDs help" />} value={promoForm.eligible_game_ids} placeholder="game_code_1, game_code_2" onChange={(event) => setPromoForm((current) => ({ ...current, eligible_game_ids: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Status" type="select" value={promoForm.status} options={PROMO_STATUS_OPTIONS} onChange={(event) => setPromoForm((current) => ({ ...current, status: event.target.value }))} />
                    <FormField label="Regulation" value={promoForm.market} placeholder="NJ" onChange={(event) => setPromoForm((current) => ({ ...current, market: event.target.value }))} />
                  </FormRow>
                  <CollapsibleFieldGroup title="Schedule">
                    <FormRow>
                      <FormField label="Starts" type="datetime-local" value={promoForm.starts_at} onChange={(event) => setPromoForm((current) => ({ ...current, starts_at: event.target.value }))} />
                      <FormField label="Ends" type="datetime-local" value={promoForm.ends_at} onChange={(event) => setPromoForm((current) => ({ ...current, ends_at: event.target.value }))} />
                    </FormRow>
                  </CollapsibleFieldGroup>
                  <CollapsibleFieldGroup title="Advanced">
                    <FormRow>
                      <FormField label="Priority" type="number" value={promoForm.priority} onChange={(event) => setPromoForm((current) => ({ ...current, priority: event.target.value }))} />
                      <FormField label="Segment" value={promoForm.segment} placeholder="vip" onChange={(event) => setPromoForm((current) => ({ ...current, segment: event.target.value }))} />
                    </FormRow>
                  </CollapsibleFieldGroup>
                  {promoForm.promo_id.trim() ? (
                    <DecisionReview
                      title={promoForm.id ? 'Review promotion changes' : 'Review new promotion'}
                      description="Confirm which games and runtime rules this promotion can influence."
                      contextItems={[
                        { label: 'Runtime status', value: promotionImpact.status },
                        { label: 'Eligible games', value: String(promotionImpact.count) },
                        { label: 'Matching rules', value: matchingRulesReviewValue || (promotionImpact.rules.length ? promotionImpact.rules.join(', ') : 'None') },
                      ]}
                      changeItems={promotionChanges}
                      consequence={rulesReviewConsequence || (promotionImpact.warnings.length
                        ? <ul className={styles.impactWarnings}>{promotionImpact.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                        : 'Saving makes this promotion available to matching active merchandising rules.')}
                      error={dataError}
                      actions={(
                        <>
                          {promoForm.id ? <Button type="button" variant="secondary" onClick={() => setPromoForm(DEFAULT_PROMO_FORM)}><X size={16} />Cancel</Button> : null}
                          <Button type="submit" disabled={savePromotion.isPending}><Save size={16} />Save promotion</Button>
                        </>
                      )}
                    />
                  ) : (
                    <div className={styles.formActions}>
                      {dataError ? <p className={styles.error} role="alert">{dataError}</p> : null}
                      {promoForm.id ? <Button type="button" variant="secondary" onClick={() => setPromoForm(DEFAULT_PROMO_FORM)}><X size={16} />Cancel</Button> : null}
                      <Button type="submit" disabled={savePromotion.isPending}><Save size={16} />Save promotion</Button>
                    </div>
                  )}
                </form>
              </FormSection>
            </Drawer>
            <FormSection title="Configured promotions">
              <DataList
                items={promotions}
                primary={(item) => item.name || item.promo_id}
                meta={(item) => [item.promo_type, item.status, `${item.eligible_game_ids?.length || 0} games`, item.market]}
                renderDetails={(item) => (
                  <RuntimeImpact
                    impact={withRuleEvidenceState(
                      buildPromotionImpact(item, rules, { rulesReady }),
                      rulesReviewState,
                    )}
                  />
                )}
                onEdit={canConfigure ? (item) => {
                  setPromoForm(promoToForm(item))
                  setEditorOpen(true)
                } : undefined}
                onDelete={canConfigure ? (id) => setDeleteCandidate({ kind: 'promotion', item: promotions.find((entry) => entry.id === id) }) : undefined}
                deletePending={deletePromotion.isPending}
              />
            </FormSection>
          </div>
        )}

        {merchandisingTab === 'jackpots' && (
          <div className={styles.stack}>
            <Drawer
              open={canConfigure && editorOpen && merchandisingTab === 'jackpots'}
              side="right"
              title={jackpotForm.id ? 'Edit jackpot pool' : 'Create jackpot pool'}
              description="Set pool participation and review its runtime effect before saving."
              onClose={() => {
                setEditorOpen(false)
                setJackpotForm(DEFAULT_JACKPOT_FORM)
                setDataError('')
              }}
            >
              <FormSection title="Jackpot pool details">
                <form className={styles.form} onSubmit={(event) => submitDataForm(event, saveJackpot, buildJackpotPayload, jackpotForm, ['pool_id'])}>
                  <FormRow>
                    <FormField label="Pool ID" required value={jackpotForm.pool_id} placeholder="daily-drop-001" onChange={(event) => setJackpotForm((current) => ({ ...current, pool_id: event.target.value }))} />
                    <FormField label="Display label" value={jackpotForm.label} placeholder="Daily Drop" onChange={(event) => setJackpotForm((current) => ({ ...current, label: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Jackpot type" type="select" value={jackpotForm.jackpot_type} options={JACKPOT_TYPE_OPTIONS.filter((option) => option.value)} onChange={(event) => setJackpotForm((current) => ({ ...current, jackpot_type: event.target.value }))} />
                    <FormField label="Regulation" value={jackpotForm.market} placeholder="NJ" onChange={(event) => setJackpotForm((current) => ({ ...current, market: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Current amount" type="number" value={jackpotForm.current_amount} placeholder="25000" onChange={(event) => setJackpotForm((current) => ({ ...current, current_amount: event.target.value }))} />
                    <FormField label="Currency" value={jackpotForm.currency} placeholder="USD" onChange={(event) => setJackpotForm((current) => ({ ...current, currency: event.target.value }))} />
                  </FormRow>
                  <FormField label="Participating game IDs" labelExtra={<HelpTip text="Comma-separated platform game codes included in this jackpot pool." label="Participating games help" />} value={jackpotForm.participating_game_ids} placeholder="game_code_1, game_code_2" onChange={(event) => setJackpotForm((current) => ({ ...current, participating_game_ids: event.target.value }))} />
                  {jackpotForm.jackpot_type === 'must_drop' ? (
                    <FormField label="Must drop at" type="datetime-local" value={jackpotForm.must_drop_at} onChange={(event) => setJackpotForm((current) => ({ ...current, must_drop_at: event.target.value }))} />
                  ) : null}
                  <CollapsibleFieldGroup title="Advanced">
                    <FormRow>
                      <FormField label="Last payout" type="datetime-local" value={jackpotForm.last_payout_at} onChange={(event) => setJackpotForm((current) => ({ ...current, last_payout_at: event.target.value }))} />
                      <FormField label="Segment" value={jackpotForm.segment} placeholder="vip" onChange={(event) => setJackpotForm((current) => ({ ...current, segment: event.target.value }))} />
                    </FormRow>
                  </CollapsibleFieldGroup>
                  {jackpotForm.pool_id.trim() ? (
                    <DecisionReview
                      title={jackpotForm.id ? 'Review jackpot changes' : 'Review new jackpot'}
                      description="Confirm the participating games and runtime rule coverage before saving."
                      contextItems={[
                        { label: 'Runtime status', value: jackpotImpact.status },
                        { label: 'Participating games', value: String(jackpotImpact.count) },
                        { label: 'Matching rules', value: matchingRulesReviewValue || (jackpotImpact.rules.length ? jackpotImpact.rules.join(', ') : 'None') },
                      ]}
                      changeItems={jackpotChanges}
                      consequence={rulesReviewConsequence || (jackpotImpact.warnings.length
                        ? <ul className={styles.impactWarnings}>{jackpotImpact.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                        : 'Saving makes this pool available to matching active jackpot rules.')}
                      error={dataError}
                      actions={(
                        <>
                          {jackpotForm.id ? <Button type="button" variant="secondary" onClick={() => setJackpotForm(DEFAULT_JACKPOT_FORM)}><X size={16} />Cancel</Button> : null}
                          <Button type="submit" disabled={saveJackpot.isPending}><Save size={16} />Save jackpot</Button>
                        </>
                      )}
                    />
                  ) : (
                    <div className={styles.formActions}>
                      {dataError ? <p className={styles.error} role="alert">{dataError}</p> : null}
                      {jackpotForm.id ? <Button type="button" variant="secondary" onClick={() => setJackpotForm(DEFAULT_JACKPOT_FORM)}><X size={16} />Cancel</Button> : null}
                      <Button type="submit" disabled={saveJackpot.isPending}><Save size={16} />Save jackpot</Button>
                    </div>
                  )}
                </form>
              </FormSection>
            </Drawer>
            <FormSection title="Configured jackpot pools">
              <DataList
                items={jackpots}
                primary={(item) => item.label || item.pool_id}
                meta={(item) => [item.jackpot_type, item.current_amount ? `${item.currency || 'USD'} ${item.current_amount}` : '', item.must_drop_at ? `Drops ${formatDate(item.must_drop_at)}` : '', item.market]}
                renderDetails={(item) => (
                  <RuntimeImpact
                    impact={withRuleEvidenceState(
                      buildJackpotImpact(item, rules, { rulesReady }),
                      rulesReviewState,
                    )}
                  />
                )}
                onEdit={canConfigure ? (item) => {
                  setJackpotForm(jackpotToForm(item))
                  setEditorOpen(true)
                } : undefined}
                onDelete={canConfigure ? (id) => setDeleteCandidate({ kind: 'jackpot', item: jackpots.find((entry) => entry.id === id) }) : undefined}
                deletePending={deleteJackpot.isPending}
              />
            </FormSection>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(deleteCandidate?.item)}
        title={`Delete ${deleteCandidate?.kind === 'jackpot' ? 'jackpot pool' : 'promotion'}`}
        message={`Delete "${deleteCandidate?.item?.name || deleteCandidate?.item?.label || deleteCandidate?.item?.promo_id || deleteCandidate?.item?.pool_id || 'this item'}"? This permanently removes it from Studio and the next runtime configuration.`}
        confirmLabel={`Delete ${deleteCandidate?.kind === 'jackpot' ? 'jackpot pool' : 'promotion'}`}
        cancelLabel="Keep item"
        loading={deletePending}
        onConfirm={() => {
          if (!deleteCandidate?.item || deletePending) return
          if (deleteCandidate.kind === 'jackpot') deleteJackpot.mutate(deleteCandidate.item.id)
          else deletePromotion.mutate(deleteCandidate.item.id)
        }}
        onCancel={() => {
          if (!deletePending) setDeleteCandidate(null)
        }}
        isDestructive
      />
    </ProductPageShell>
  )
}

export default IGamingMerchandisingPage
